import { useState } from 'react'
import { Calendar } from 'lucide-react'
import { Spinner, formatDate } from '@dogdisk/shared'
import { AgentLayout } from '../components/agent-layout'
import { useMyUsers, type SubUserRow } from '../_lib/agent-api'

const PAGE_SIZE = 10

export function AgentUsersPage() {
  const [page, setPage] = useState(1)

  // 精简后(2026-07-01):仅直接下级(first_agent_id = 当前代理),后端已保证语义。
  const { data, isLoading } = useMyUsers('all', 500, 0)

  const items = data?.users ?? []
  const total = data?.total ?? items.length
  const totalPages = Math.max(1, Math.ceil(items.length / PAGE_SIZE))
  const pageItems = items.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <AgentLayout>
      <div data-testid="wWJ6V" className="flex flex-col gap-5 p-6">
        {/* 筛选栏(仅注册日期区间) */}
        <section className="flex items-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-3">
          <FilterChip>
            <Calendar size={14} className="text-gray-400" />
            <span>注册开始日期</span>
          </FilterChip>
          <span className="text-gray-400">—</span>
          <FilterChip>
            <Calendar size={14} className="text-gray-400" />
            <span>注册结束日期</span>
          </FilterChip>
        </section>

        {/* 表格(UUID / 用户名 / 注册时间) */}
        <section className="rounded-xl border border-gray-200 bg-white p-5">
          <h2 className="pb-3 text-sm font-semibold text-gray-900">下级用户列表</h2>
          {isLoading ? (
            <Spinner className="text-amber-500" />
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-gray-200">
                <tr className="text-left text-xs font-semibold text-gray-500">
                  <th className="py-2.5 w-[330px]">UUID</th>
                  <th className="py-2.5">用户名</th>
                  <th className="py-2.5">昵称</th>
                  <th className="py-2.5">注册时间</th>
                </tr>
              </thead>
              <tbody>
                {pageItems.map((u) => (
                  <UserRow key={u.user_id} u={u} />
                ))}
                {pageItems.length === 0 && (
                  <tr>
                    <td colSpan={4} className="py-12 text-center text-sm text-gray-400">
                      暂无下级用户
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {/* 分页 */}
          <div className="flex items-center justify-between border-t border-gray-100 pt-3 mt-3">
            <span className="text-xs text-gray-400">
              共 {total} 条记录，第 {page}/{totalPages} 页
            </span>
            <SimplePager page={page} total={totalPages} onChange={setPage} />
          </div>
        </section>
      </div>
    </AgentLayout>
  )
}

function FilterChip({ children }: { children: React.ReactNode }) {
  return (
    <div className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-[13px] text-gray-500">
      {children}
    </div>
  )
}

function UserRow({ u }: { u: SubUserRow }) {
  return (
    <tr className="border-b border-gray-100 last:border-b-0">
      <td className="py-3 font-mono text-xs text-gray-500" title={u.user_id}>
        {u.user_id}
      </td>
      <td className="py-3 text-[13px] text-gray-700">{u.username_masked}</td>
      <td className="py-3 text-[13px] text-gray-700">{u.nickname}</td>
      <td className="py-3 text-[13px] text-gray-400">{formatDate(u.registered_at)}</td>
    </tr>
  )
}

function SimplePager({
  page,
  total,
  onChange,
}: {
  page: number
  total: number
  onChange: (p: number) => void
}) {
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        disabled={page <= 1}
        onClick={() => onChange(page - 1)}
        className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
      >
        上一页
      </button>
      <span
        className="rounded-md px-3 py-1 text-xs font-medium text-white"
        style={{ backgroundColor: '#F59E0B' }}
      >
        {page}
      </span>
      <button
        type="button"
        disabled={page >= total}
        onClick={() => onChange(page + 1)}
        className="rounded-md border border-gray-300 bg-white px-2.5 py-1 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-40"
      >
        下一页
      </button>
    </div>
  )
}
